<template>
  <div
    ref="dialog"
    class="modal fade show d-block planning-detail-backdrop"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    :aria-label="planning.title"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content planning-detail-content border-0 shadow">
        <!-- Status bar -->
        <div :class="'bg-' + getStatusColor(planning.status)" style="height: 5px;"></div>

        <div class="modal-header border-0 pb-0 pt-4 px-4">
          <div class="d-flex flex-column w-100">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <span class="text-muted small fw-bold">#{{ planning.id ? planning.id.toString().substring(0, 8) : '' }}</span>
                <h4 class="fw-bold mb-1 mt-1">{{ planning.title }}</h4>
              </div>
              <button type="button" class="btn-close" :aria-label="$t('close')" @click="$emit('close')"></button>
            </div>
            <div class="d-flex gap-2 mb-3">
              <span :class="'badge rounded-pill px-3 py-2 bg-' + getStatusColor(planning.status)">
                {{ $t(planning.status) }}
              </span>
              <span v-if="planning.priority" :class="'badge rounded-pill px-3 py-2 bg-' + getPriorityColor(planning.priority) + '-soft text-' + getPriorityColor(planning.priority)">
                {{ $t(planning.priority) }}
              </span>
            </div>
          </div>
        </div>

        <div class="modal-body px-4 pt-0">
          <div v-if="loading" class="detail-loading mb-3" aria-live="polite">
            <span class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
            <span>{{ $t('loading') }}...</span>
          </div>

          <!-- Description -->
          <div v-if="planning.description" class="mb-4">
            <label class="form-label small text-muted fw-bold text-uppercase" style="font-size: 0.65rem; letter-spacing: 0.5px;">{{$t('description')}}</label>
            <p class="mb-0" style="white-space: pre-wrap;">{{ planning.description }}</p>
          </div>

          <!-- Info Grid -->
          <div class="row g-3 mb-4">
            <!-- Client -->
            <div class="col-12 col-md-6" v-if="planning.client && planning.client.name">
              <div class="planning-info-card d-flex align-items-center p-3 bg-light rounded-3">
                <div class="icon-circle bg-white me-3 shadow-sm">
                  <AppIcon name="bi-person" class="bi bi-person text-primary" />
                </div>
                <div class="min-width-0">
                  <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('client')}}</small>
                  <span class="text-dark fw-medium d-block">{{ planning.client.name }}</span>
                  <a v-if="planning.client.phone" :href="'tel:' + planning.client.phone" class="client-contact d-block text-truncate">
                    <AppIcon name="bi-telephone" class="bi bi-telephone me-1" aria-hidden="true" />{{ planning.client.phone }}
                  </a>
                  <a v-if="planning.client.email" :href="'mailto:' + planning.client.email" class="client-contact d-block text-truncate">
                    <AppIcon name="bi-envelope" class="bi bi-envelope me-1" aria-hidden="true" />{{ planning.client.email }}
                  </a>
                </div>
              </div>
            </div>

            <!-- Date -->
            <div class="col-6 col-md-6">
              <div class="planning-info-card d-flex align-items-center p-3 bg-light rounded-3">
                <div class="icon-circle bg-white me-3 shadow-sm">
                  <AppIcon name="bi-calendar3" class="bi bi-calendar3 text-primary" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('date')}}</small>
                  <span class="text-dark fw-medium">{{ formatDate(planning.date) }}</span>
                </div>
              </div>
            </div>

            <!-- Time -->
            <div class="col-6 col-md-6">
              <div class="planning-info-card d-flex align-items-center p-3 bg-light rounded-3">
                <div class="icon-circle bg-white me-3 shadow-sm">
                  <AppIcon name="bi-clock" class="bi bi-clock text-primary" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('time')}}</small>
                  <span class="text-dark fw-medium">{{ planning.start_time || '--:--' }} - {{ planning.end_time || '--:--' }}</span>
                </div>
              </div>
            </div>

            <!-- Assigned To -->
            <div class="col-12 col-md-6" v-if="planning.assignedUser">
              <div class="planning-info-card d-flex align-items-center p-3 bg-light rounded-3">
                <div class="icon-circle bg-white me-3 shadow-sm">
                  <AppIcon name="bi-person-badge" class="bi bi-person-badge text-primary" />
                </div>
                <div>
                  <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('assigned_to')}}</small>
                  <span class="text-dark fw-medium">{{ planning.assignedUser.name }}</span>
                </div>
              </div>
            </div>

            <!-- Address -->
            <div class="col-12" v-if="planning.address">
              <div class="planning-info-card d-flex align-items-center p-3 bg-light rounded-3">
                <div class="icon-circle bg-white me-3 shadow-sm">
                  <AppIcon name="bi-geo-alt" class="bi bi-geo-alt text-primary" />
                </div>
                <div class="flex-grow-1">
                  <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('address')}}</small>
                  <span class="text-dark fw-medium">{{ planning.address }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Instructions -->
          <div v-if="planning.instructions" class="mb-4">
            <div class="instruction-box p-3 rounded-3 border-start border-4 border-primary" style="background-color: #f0f4ff;">
              <label class="form-label small text-primary fw-bold text-uppercase mb-2" style="font-size: 0.65rem; letter-spacing: 0.5px;">{{$t('instructions')}}</label>
              <p class="mb-0 text-dark" style="white-space: pre-wrap;">{{ planning.instructions }}</p>
            </div>
          </div>

          <!-- Feedback -->
          <div v-if="planning.feedback" class="mb-4">
            <div class="p-3 rounded-3 border-start border-4 border-success" style="background-color: #f0fff4;">
              <label class="form-label small text-success fw-bold text-uppercase mb-2" style="font-size: 0.65rem; letter-spacing: 0.5px;">{{$t('field_feedback')}}</label>
              <p class="mb-0 text-dark" style="white-space: pre-wrap;">{{ planning.feedback }}</p>
            </div>
          </div>

          <!-- Photos -->
          <div v-if="planning.attachments && planning.attachments.length" class="mb-4">
            <label class="form-label small text-muted fw-bold text-uppercase" style="font-size: 0.65rem; letter-spacing: 0.5px;">{{$t('attachments')}}</label>
            <div class="d-flex flex-wrap gap-2">
              <div v-for="att in planning.attachments" :key="att.id" class="position-relative">
                <button v-if="att.filetype && att.filetype.includes('image')" type="button" class="attachment-preview" @click="openAttachment(att.filepath)">
                  <img :src="att.filepath" :alt="att.filename || $t('attachments')">
                </button>
                <a v-else :href="att.filepath" target="_blank" rel="noopener noreferrer" class="attachment-file">
                  <AppIcon name="bi-file-earmark" class="bi bi-file-earmark fs-4 text-muted" />
                </a>
              </div>
            </div>
          </div>

          <div v-if="planning.activities && planning.activities.length" class="activity-section">
            <div class="section-heading">
              <div>
                <small>{{ $t('history') }}</small>
                <h6 class="mb-0">{{ $t('history') }}</h6>
              </div>
              <span class="activity-count">{{ planning.activities.length }}</span>
            </div>

            <div class="activity-list">
              <div v-for="activity in sortedActivities" :key="activity.id" class="activity-item">
                <span class="activity-marker" :class="'activity-marker--' + (activity.activity_type || activity.type || 'default')">
                  <AppIcon :name="getActivityIcon(activity.activity_type || activity.type)" :class="getActivityIcon(activity.activity_type || activity.type)" aria-hidden="true" />
                </span>
                <div class="activity-content">
                  <div class="activity-meta">
                    <strong>{{ activity.user ? activity.user.name : '-' }}</strong>
                    <span>{{ formatDateTime(activity.created_at) }}</span>
                  </div>
                  <p class="mb-0">{{ formatActivityDescription(activity) }}</p>
                  <div v-if="activity.old_value && activity.new_value" class="activity-change">
                    <span>{{ $t(activity.old_value) }}</span>
                    <AppIcon :name="$i18n.locale === 'ar' ? 'bi bi-arrow-left' : 'bi bi-arrow-right'" :class="$i18n.locale === 'ar' ? 'bi bi-arrow-left' : 'bi bi-arrow-right'" aria-hidden="true" />
                    <span>{{ $t(activity.new_value) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer d-block border-0 px-4 pb-4 pt-0">
          <div v-if="editable || deletable" class="d-flex gap-2 mb-2">
            <button
              v-if="deletable"
              type="button"
              class="btn btn-outline-danger rounded-3 me-auto"
              :disabled="deleting"
              @click="$emit('delete', planning)"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
              <AppIcon name="bi-trash" v-else class="bi bi-trash me-1" aria-hidden="true" />
              {{ $t('delete') }}
            </button>
            <button v-if="editable" type="button" class="btn btn-primary rounded-3" @click="$emit('edit', planning)">
              <AppIcon name="bi-pencil" class="bi bi-pencil me-1" aria-hidden="true" />
              {{ $t('edit') }}
            </button>
          </div>
          <button type="button" class="btn btn-outline-secondary rounded-3 w-100" @click="$emit('close')">
            {{ $t('close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'

export default {
  name: 'PlanningDetailModal',
  props: {
    planning: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    deletable: {
      type: Boolean,
      default: false
    },
    deleting: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    sortedActivities() {
      return [...(this.planning.activities || [])]
        .sort((first, second) => new Date(second.created_at) - new Date(first.created_at))
    }
  },
  mounted() {
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', this.handleKeydown)
    this.$nextTick(() => this.$refs.dialog && this.$refs.dialog.focus())
  },
  beforeDestroy() {
    document.body.style.overflow = this.previousBodyOverflow || ''
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    formatDate(d) {
      return helper.formatDate(d)
    },
    formatDateTime(d) {
      return helper.formatDateTime(d)
    },
    formatActivityDescription(activity) {
      if (activity.activity_type === 'status_change' && activity.new_value) {
        return activity.old_value
          ? this.$t('status_changed_from_to', {
            from: this.$t(activity.old_value),
            to: this.$t(activity.new_value)
          })
          : this.$t('status_updated_to', { status: this.$t(activity.new_value) })
      }
      if (activity.activity_type === 'update' && activity.description === 'Planning created') {
        return this.$t('planning_created')
      }
      return activity.description
    },
    getStatusColor(status) {
      const colors = {
        planned: 'primary',
        in_progress: 'info',
        completed: 'success',
        failed: 'danger',
        postponed: 'warning'
      }
      return colors[status] || 'secondary'
    },
    getPriorityColor(priority) {
      const colors = { low: 'info', medium: 'primary', high: 'warning', urgent: 'danger' }
      return colors[priority] || 'primary'
    },
    getActivityIcon(type) {
      return {
        status_change: 'bi bi-arrow-repeat',
        comment: 'bi bi-chat-dots',
        update: 'bi bi-pencil'
      }[type] || 'bi bi-circle'
    },
    openAttachment(url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.$emit('close')
    }
  }
}
</script>

<style scoped>
.planning-detail-backdrop {
  z-index: 1060;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(3px);
}

.planning-detail-content {
  overflow: hidden;
  border-radius: 18px;
}

.planning-info-card {
  height: 100%;
  border: 1px solid #edf0f3;
  background: #f8fafc !important;
}

.detail-loading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.78rem;
  font-weight: 650;
}

.min-width-0 {
  min-width: 0;
}

.client-contact {
  color: #667085;
  font-size: 0.72rem;
  text-decoration: none;
}

.client-contact:hover,
.client-contact:focus-visible {
  color: #0d6efd;
  text-decoration: underline;
}

.icon-circle {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bg-primary-soft { background-color: #e7f1ff; }
.bg-success-soft { background-color: #e9fbf0; }
.bg-info-soft { background-color: #e7faff; }
.bg-warning-soft { background-color: #fff8e6; }
.bg-danger-soft { background-color: #ffeef0; }

.attachment-preview,
.attachment-file {
  width: 84px;
  height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.attachment-preview:hover,
.attachment-preview:focus-visible,
.attachment-file:hover,
.attachment-file:focus-visible {
  transform: translateY(-2px);
  outline: none;
  box-shadow: 0 7px 18px rgba(31, 41, 55, 0.14);
}

.attachment-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.activity-section {
  padding-top: 1rem;
  border-top: 1px solid #edf0f3;
}

.section-heading,
.activity-meta,
.activity-change {
  display: flex;
  align-items: center;
}

.section-heading {
  justify-content: space-between;
  margin-bottom: 1rem;
}

.section-heading small {
  color: #98a2b3;
  font-size: 0.63rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-heading h6 {
  color: #1d2939;
  font-weight: 750;
}

.activity-count {
  min-width: 28px;
  padding: 0.22rem 0.48rem;
  border-radius: 999px;
  background: #f2f4f7;
  color: #667085;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: center;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.activity-item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 0.7rem;
}

.activity-marker {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #eef5ff;
  color: #0d6efd;
}

.activity-marker--comment {
  background: #ecfdf3;
  color: #198754;
}

.activity-content {
  padding: 0.7rem 0.8rem;
  border: 1px solid #edf0f3;
  border-radius: 10px;
  background: #fafbfc;
}

.activity-meta {
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.activity-meta strong,
.activity-content p {
  color: #344054;
  font-size: 0.77rem;
}

.activity-meta span {
  color: #98a2b3;
  font-size: 0.65rem;
}

.activity-change {
  gap: 0.45rem;
  margin-top: 0.45rem;
  color: #667085;
  font-size: 0.7rem;
}

@media (max-width: 575.98px) {
  .planning-detail-backdrop {
    padding: 0.75rem;
  }

  .planning-detail-content {
    width: 100%;
    max-height: calc(100dvh - 1.5rem);
    border-radius: 16px;
  }

  .modal-dialog {
    align-items: center;
    min-height: 100%;
    margin: 0;
  }

  .modal-header {
    padding: 1rem 1rem 0 !important;
  }

  .modal-header h4 {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 0 1rem 1rem !important;
  }

  .modal-body > .row {
    --bs-gutter-x: 0.5rem;
    --bs-gutter-y: 0.5rem;
    margin-bottom: 0.75rem !important;
  }

  .planning-info-card {
    min-height: 72px;
    padding: 0.75rem !important;
  }

  .planning-info-card .icon-circle {
    width: 32px;
    height: 32px;
    margin-inline-end: 0.6rem !important;
  }

  .planning-info-card .fw-medium {
    font-size: 0.88rem;
  }

  .modal-body > .mb-4 {
    margin-bottom: 0.75rem !important;
  }

  .modal-footer {
    padding: 0.75rem 1rem 1rem !important;
    border-top: 1px solid #edf0f3 !important;
  }

  .activity-meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.1rem;
  }
}
</style>
